import { useNavigate, useParams } from "react-router-dom";
import { GraduationCap, Landmark, Scale, PiggyBank, HandCoins, Calculator, ShieldCheck, ChevronRight } from "lucide-react";
import { SubHeader } from "@/components/Brand";
import { useApi } from "@/lib/api";

export const EDU_ICONS = {
  cu: [Landmark, "from-blue-500 to-blue-700"], hak: [Scale, "from-violet-500 to-purple-700"], simpanan: [PiggyBank, "from-emerald-500 to-teal-700"],
  pinjaman: [HandCoins, "from-orange-400 to-orange-600"], keuangan: [Calculator, "from-sky-400 to-blue-600"], keamanan: [ShieldCheck, "from-rose-500 to-red-700"],
};

export const ArticleBody = ({ body }) => (
  <div className="space-y-3 text-[15px] leading-relaxed text-slate-800" data-testid="edu-article-body">
    {body.split(/\n{2,}/).map((block, i) => {
      const lines = block.split("\n");
      return (
        <div key={i} className="space-y-1.5">
          {lines.map((l, j) => l.startsWith("## ") ? <h3 key={j} className="font-display font-bold text-lg text-slate-900 pt-1">{l.slice(3)}</h3>
            : l.startsWith("- ") ? <div key={j} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" /><span>{l.slice(2)}</span></div>
            : <p key={j}>{l}</p>)}
        </div>
      );
    })}
  </div>
);

export function EduList() {
  const navigate = useNavigate();
  const { data } = useApi("/edu/articles");
  return (
    <div data-testid="edu-page">
      <SubHeader title="Pendidikan Anggota" onBack={() => navigate("/app")} />
      <div className="px-5 space-y-3">
        <div className="rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 text-white p-4 flex items-center gap-3 shadow-lg">
          <GraduationCap className="h-9 w-9 shrink-0" />
          <p className="text-sm">Pendidikan adalah pilar utama Credit Union. Pelajari hak, kewajiban, dan cara mengelola keuangan bersama CU.</p>
        </div>
        {data?.map((a) => {
          const [Icon, tone] = EDU_ICONS[a.icon] || EDU_ICONS.cu;
          return (
            <button key={a.article_id} onClick={() => navigate(`/app/edukasi/${a.article_id}`)} data-testid={`edu-article-${a.article_id}`}
              className="w-full text-left bg-white border border-slate-200 rounded-2xl p-3.5 flex items-center gap-3 hover:border-rose-300 transition-colors">
              <span className={`h-12 w-12 rounded-xl bg-gradient-to-br ${tone} text-white flex items-center justify-center shrink-0`}><Icon className="h-6 w-6" /></span>
              <span className="flex-1 min-w-0"><span className="block font-semibold text-slate-900">{a.title}</span><span className="block text-xs text-slate-500">{a.summary}</span></span>
              <ChevronRight className="h-5 w-5 text-slate-400" />
            </button>
          );
        })}
        {data?.length === 0 && <p className="text-sm text-center text-slate-500 py-8" data-testid="edu-empty">Belum ada materi.</p>}
      </div>
    </div>
  );
}

export function EduArticle() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: a } = useApi(`/edu/articles/${id}`);
  if (!a) return <div className="p-8 text-center text-slate-500">Memuat...</div>;
  const [Icon, tone] = EDU_ICONS[a.icon] || EDU_ICONS.cu;
  return (
    <div data-testid="edu-article-page">
      <SubHeader title="Pendidikan Anggota" onBack={() => navigate("/app/edukasi")} />
      <article className="px-5 space-y-4">
        <div className={`rounded-2xl bg-gradient-to-br ${tone} text-white p-5`}>
          <Icon className="h-9 w-9" />
          <h1 className="font-display font-extrabold text-2xl mt-2" data-testid="edu-article-title">{a.title}</h1>
          <p className="text-sm text-white/90">{a.summary}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5"><ArticleBody body={a.body} /></div>
      </article>
    </div>
  );
}
